import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useContext, useEffect, useState } from 'react';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { useDebounce } from 'hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { FilterModel, getApplicableBedRules } from 'ts/hotel/classes/FilterModel';
import { faBed, faChevronDown, faChevronUp, faHistory, faHotel, faPlusCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import SearchHistory from 'components/shared/SearchHistory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import FilterResultPrice2 from './FilterResultPrice2';
import { MapHotelContext } from 'contexts/MapHotelContext';
import { useSearchParams } from 'react-router-dom';
import FilterResultFacilities from './FilterResultFacilities';
import { Hotel } from 'ts/hotel/interfaces/HotelResponse';
import Tabs from 'components/ui/Tabs/Tabs';

interface FilterResultLeftProps {
  hotels?: Hotel[];
  onChangeFilter: (filter: FilterModel) => void;
}

const MAX_FILTER_OR = 3;

const joinFilterValues = (values: string[]) =>
  values.map((value) => value.trim()).filter(Boolean).join('|');

const splitFilterValues = (value?: string) => {
  const values = value ? value.split('|') : [''];
  return values.slice(0, MAX_FILTER_OR);
};

const syncFilterValues = (current: string[], nextValue?: string) => {
  const nextValues = splitFilterValues(nextValue);
  if (joinFilterValues(current) === joinFilterValues(nextValues)) {
    return current;
  }
  return nextValues.length ? nextValues : [''];
};

const FilterResultLeft: FC<FilterResultLeftProps> = ({ hotels, onChangeFilter }) => {
  const { filter } = useContext(HotelFilterContext);
  const { pinnedHotelsIds } = useContext(MapHotelContext);
  const { t } = useTranslation();
  const [urlParams] = useSearchParams();
  const [nameValues, setNameValues] = useState(splitFilterValues(filter?.name));
  const [roomValues, setRoomValues] = useState(splitFilterValues(filter?.room));
  const [expandedBedRuleIndexes, setExpandedBedRuleIndexes] = useState<number[]>([]);
  const [activeLeftTab, setActiveLeftTab] = useState<'rooms' | 'facilities' | 'history'>('rooms');
  const hasPinnedHotels = pinnedHotelsIds.length > 0;
  const applicableBedRules = getApplicableBedRules(
    filter?.bedRules,
    urlParams.get('roomsString') || localStorage.getItem('roomsStringHotel') || '',
  );
  const bedFilterRules = applicableBedRules
    .flatMap((group) => group.items || [])
    .filter((rule) => !!rule.positive?.length);

  useEffect(() => {
    if (!filter) {
      return;
    }

    setNameValues((current) => syncFilterValues(current, filter.name));
    setRoomValues((current) => syncFilterValues(current, filter.room));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.name, filter?.room]);

  const debouncedOnChangeFilter = useDebounce((updatedFilter: FilterModel) => {
    onChangeFilter(updatedFilter);
  }, 500);

  const handleNameChange = (index: number, name: string) => {
    const nextValues = [...nameValues];
    nextValues[index] = name;
    setNameValues(nextValues);
    if (filter) {
      debouncedOnChangeFilter({ ...filter, name: joinFilterValues(nextValues) });
    }
  };

  const handleAddNameLine = () => {
    if (nameValues.length >= MAX_FILTER_OR || !nameValues[nameValues.length - 1]) {
      return;
    }
    setNameValues([...nameValues, '']);
  };

  const handleRemoveName = (index: number) => {
    const nextValues = nameValues.length === 1 ? [''] : nameValues.filter((_, i) => i !== index);
    setNameValues(nextValues);
    if (filter) {
      onChangeFilter({ ...filter, name: joinFilterValues(nextValues) });
    }
  };

  const handleRoomChange = (index: number, room: string) => {
    const nextValues = [...roomValues];
    nextValues[index] = room;
    setRoomValues(nextValues);
    if (filter) {
      debouncedOnChangeFilter({ ...filter, room: joinFilterValues(nextValues) });
    }
  };

  const handleAddRoomLine = () => {
    if (roomValues.length >= MAX_FILTER_OR || !roomValues[roomValues.length - 1]) {
      return;
    }
    setRoomValues([...roomValues, '']);
  };

  const handleRemoveRoom = (index: number) => {
    const nextValues = roomValues.length === 1 ? [''] : roomValues.filter((_, i) => i !== index);
    setRoomValues(nextValues);
    if (filter) {
      onChangeFilter({ ...filter, room: joinFilterValues(nextValues) });
    }
  };

  const getBedFilterFromInclude = (include: string[]) => {
    const includeSet = new Set(include);
    const exclude = Array.from(
      new Set(
        bedFilterRules.flatMap((rule) => {
          const isActive = (rule.positive || []).some((option) => includeSet.has(option));
          return isActive ? rule.negative || [] : [];
        }),
      ),
    );

    return { include, exclude };
  };

  const handleBedIncludeChange = (option: string, checked: boolean) => {
    if (!filter) {
      return;
    }

    const current = filter.bedFilter || { include: [], exclude: [] };
    const nextInclude = checked
      ? current.include.includes(option)
        ? current.include
        : [...current.include, option]
      : current.include.filter((value) => value !== option);

    onChangeFilter({
      ...filter,
      bedFilter: getBedFilterFromInclude(nextInclude),
    });
  };

  const handleBedIncludeGroupChange = (options: string[], checked: boolean) => {
    if (!filter) {
      return;
    }

    const current = filter.bedFilter || { include: [], exclude: [] };
    const optionSet = new Set(options);
    const nextInclude = checked
      ? Array.from(new Set([...current.include, ...options]))
      : current.include.filter((value) => !optionSet.has(value));

    onChangeFilter({
      ...filter,
      bedFilter: getBedFilterFromInclude(nextInclude),
    });
  };

  const toggleBedRuleExpanded = (index: number) => {
    setExpandedBedRuleIndexes((current) =>
      current.includes(index) ? current.filter((value) => value !== index) : [...current, index],
    );
  };

  return (
    <div className="hotel-result-filter-left">
      <Tabs
        label={t('filter.filter')}
        className="hotels-tabs hotels-main-tabs hotel-result-filter-left-tabs w100p"
        value={activeLeftTab}
        onValueChange={(v) => setActiveLeftTab(v as typeof activeLeftTab)}
        items={[
          {
            value: 'rooms',
            label: (
              <span className="display-flex">
                <Tooltip position="top" content={t('hotel.rooms')}>
                  <FontAwesomeIcon size="sm" icon={faBed} />
                </Tooltip>
              </span>
            ),
          },
          {
            value: 'facilities',
            label: (
              <span className="display-flex">
                <Tooltip position="top" content={t('hotel.facilities')}>
                  <FontAwesomeIcon size="sm" icon={faHotel} />
                </Tooltip>
              </span>
            ),
          },
          {
            value: 'history',
            label: (
              <span className="display-flex">
                <Tooltip position="top" content={t('search.your_latest_searches')}>
                  <FontAwesomeIcon size="sm" icon={faHistory} />
                </Tooltip>
              </span>
            ),
          },
        ]}
      />

      {!!filter && (
        <Fragment>
          {activeLeftTab === 'rooms' && (
            <Fragment>
              {nameValues.map((nameValue, index) => (
                <Fragment key={index}>
                  <div className="position-relative hotel-result-filter-input name mb-10">
                    {!!nameValue && (
                      <FontAwesomeIcon
                        icon={faTimes}
                        className="hotel-result-filter-clearsm"
                        onClick={() => handleRemoveName(index)}
                      />
                    )}
                    {index === nameValues.length - 1 &&
                      !!nameValue &&
                      nameValues.length < MAX_FILTER_OR && (
                        <div className="hotel-result-filter-name-add" onClick={handleAddNameLine}>
                          <FontAwesomeIcon icon={faPlusCircle} />
                        </div>
                      )}
                    <FormGroup
                      label=""
                      value={nameValue}
                      onChange={(name) => handleNameChange(index, name)}
                      movablePlaceholder={t('filter.by_name')}
                      movablePlaceholderIcon={faHotel}
                      movablePlaceholderSm={true}
                    />
                  </div>
                </Fragment>
              ))}

              {roomValues.map((roomValue, index) => (
                <Fragment key={index}>
                  <div className="position-relative hotel-result-filter-input rooms mb-10">
                    {!!roomValue && (
                      <FontAwesomeIcon
                        icon={faTimes}
                        className="hotel-result-filter-clearsm"
                        onClick={() => handleRemoveRoom(index)}
                      />
                    )}
                    {index === roomValues.length - 1 &&
                      !!roomValue &&
                      roomValues.length < MAX_FILTER_OR && (
                        <div className="hotel-result-filter-name-add" onClick={handleAddRoomLine}>
                          <FontAwesomeIcon icon={faPlusCircle} />
                        </div>
                      )}
                    <FormGroup
                      label=""
                      value={roomValue}
                      onChange={(room) => handleRoomChange(index, room)}
                      movablePlaceholder={t('filter.by_rooms')}
                      movablePlaceholderIcon={faBed}
                      movablePlaceholderSm={true}
                    />
                  </div>
                </Fragment>
              ))}

              <FilterResultPrice2 filter={filter} onFilterChange={onChangeFilter} />

              {!!filter.hType?.options.length && <div className='prices-filter mt-10'>
                <div className='title-underline text-st mb-10 title-header-grey'>{t('hotel.hotel_type')}</div>
                <div className='h-type-filter'>
                  {filter.hType.options.map((option, index) => (
                    <Checkbox
                      key={option}
                      className='mb-10'
                      label={`${t(filter.hType?.labels?.[index] || '')} (${filter.hType?.counts?.[index] ?? 0})`}
                      checked={!!filter.hType?.value?.includes(option)}
                      onChange={(checked) => {
                        const newValues = [...filter.hType?.value || []];
                        if (checked) {
                          if (!newValues.includes(option)) {
                            newValues.push(option);
                          }
                        } else {
                          const optionIndex = newValues.indexOf(option);
                          if (optionIndex >= 0) {
                            newValues.splice(optionIndex, 1);
                          }
                        }
                        onChangeFilter({ ...filter, hType: { ...filter.hType!, value: newValues } });
                      }}
                    />
                  ))}
                </div>
              </div>
              }


              {!!bedFilterRules.length && (
                <div className="prices-filter mt-10">
                  <div className="title-underline text-st mb-10 title-header-grey">
                    {t('bed_filter.title')}
                  </div>
                  {bedFilterRules.map((rule, index) => {
                    const options = rule.positive || [];
                    const hasMultiple = options.length > 1;
                    const includeList = filter.bedFilter?.include || [];
                    const allSelected = options.every((option) => includeList.includes(option));
                    const isExpanded = expandedBedRuleIndexes.includes(index);

                    return (
                      <div
                        key={index}
                        className={index < bedFilterRules.length - 1 ? 'mb-10' : ''}
                      >
                        <div className="h-type-filter">
                          <Checkbox
                            className="mb-5"
                            checked={allSelected}
                            onChange={(checked) => handleBedIncludeGroupChange(options, checked)}
                            htmlLabel={
                              <span className="flex-v-center flex-between w100p">
                                <span>
                                  {rule.title_lang}
                                </span>
                                {hasMultiple && (
                                  <span
                                    className="ml-5 cur-poi"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      event.preventDefault();
                                      toggleBedRuleExpanded(index);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                  </span>
                                )}
                              </span>
                            }
                          />
                          {hasMultiple &&
                            isExpanded &&
                            options.map((option, optionIndex) => (
                              <Checkbox
                                key={optionIndex}
                                className="mb-5 ml-10"
                                label={option}
                                checked={includeList.includes(option)}
                                onChange={(checked) => handleBedIncludeChange(option, checked)}
                              />
                            ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="prices-filter mt-10">
                <Checkbox
                  label={t('bed_filter.pin_only')}
                  checked={!!filter.pinOnly && hasPinnedHotels}
                  disabled={!hasPinnedHotels}
                  onChange={(checked) => onChangeFilter({ ...filter, pinOnly: checked })}
                />
              </div>
            </Fragment>
          )}

          {activeLeftTab === 'facilities' && (
            <FilterResultFacilities hotels={hotels} onChangeFilter={onChangeFilter} />
          )}

          {activeLeftTab === 'history' && (
            <SearchHistory productOverride="Hotels" hideHeader />
          )}

        </Fragment>
      )}
    </div>
  );
};

export default withErrorBoundary(FilterResultLeft, 'FilterResultLeft');
